import { fromEvent, interval, merge, Observable, Subject } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { InjectionToken } from '@angular/core';
import { isDefined } from '@fi-shared/utils';

export const VISIBILITY_HELPER = new InjectionToken<VisibilityHelper>('VISIBILITY_HELPER');

interface IVisibilityData {
    element: HTMLElement;
    onVisibilityChanged: (isVisible: boolean) => void;
    lastIsVisible?: boolean;
}

enum VisibilitySize {
    Half = 0,
}

export class VisibilityHelper {
    private items: IVisibilityData[] = [];

    constructor(scrollableElement: HTMLElement) {
        const scrollableElements: HTMLElement[] = [];
        // get all scrollable parents
        let currentElement = scrollableElement;
        while (currentElement) {
            if (currentElement.hasAttribute('fi-scroll-root')) {
                scrollableElements.push(currentElement);
            }
            currentElement = currentElement.parentElement;
        }

        const scrollObservables: Observable<Event>[] = scrollableElements.map((scrollableElement) => {
            return fromEvent(scrollableElement, 'scroll');
        });
        merge(...scrollObservables)
            .pipe(debounce(() => interval(200)))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((v) => this.checkVisibility());
        setTimeout(() => this.checkVisibility());
    }

    addElementToCheckVisibilityQueue(element: HTMLElement, onVisibilityChanged: (isVisible: boolean) => void) {
        const item: IVisibilityData = {
            element: element,
            onVisibilityChanged: onVisibilityChanged,
        };
        this.items.push(item);

        // return function for removing element from queue
        return () => {
            const idx = this.items.indexOf(item);
            if (idx !== -1) {
                this.items.splice(idx, 1);
            }
        };
    }

    private checkVisibility(size: VisibilitySize = VisibilitySize.Half) {
        this.items.forEach((item) => {
            const rect = item.element.getBoundingClientRect();
            const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
            let minVisibleSize = 0;

            // with minimum height settings = "half" element should be scrolled to half of his own height
            // add here some other options or parting of percents/pixels if needed
            if (isDefined(size)) {
                switch (size) {
                    case VisibilitySize.Half:
                        const elementHeight = rect.height;
                        minVisibleSize = (elementHeight > viewHeight ? viewHeight : elementHeight) / 2;
                        break;
                }
            }
            const isVisible = !(rect.bottom < 0 || rect.top + minVisibleSize - viewHeight >= 0);
            if (isVisible !== item.lastIsVisible) {
                item.lastIsVisible = isVisible;
                item.onVisibilityChanged(isVisible);
            }
        });
    }

    private onDestroy$ = new Subject<void>();
    onDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
